import type { Store } from '@blocksuite/affine/store';

const INITIAL_TARGET_FOLDER_ID_META_KEY = 'streamlain:initialTargetFolderId';

type SnapshotImportDocEngine = {
  flushAndWaitForSynced(docId: string): Promise<void>;
};

type SnapshotImportDocsService = {
  notifyDocCreated(docId: string): void;
  waitForServerConfirmed(
    docId: string,
    options?: { timeoutMs?: number; pollMs?: number }
  ): Promise<boolean>;
};

function isDatabaseRootDocId(docId: string): boolean {
  return (
    docId.startsWith('db$') &&
    docId.includes('$database$') &&
    docId.endsWith('$root')
  );
}

/**
 * Snapshot exports do not carry workspace folder topology. An imported
 * document's creation-time folder hint therefore belongs to the source
 * workspace and must never be materialized in the destination workspace.
 */
export function normalizeSnapshotImportStores(stores: readonly Store[]): void {
  for (const store of stores) {
    const metadata = store.spaceDoc.getMap('meta');
    metadata.delete(INITIAL_TARGET_FOLDER_ID_META_KEY);
    if (metadata.has(INITIAL_TARGET_FOLDER_ID_META_KEY)) {
      throw new Error(
        `Snapshot document "${store.id}" retained source workspace folder placement.`
      );
    }
  }
}

export function snapshotImportPageDocIds(stores: readonly Store[]): string[] {
  return stores
    .filter(store => !store.id.startsWith('db$'))
    .map(store => store.id);
}

export function snapshotImportSystemDocIds(stores: readonly Store[]): string[] {
  return stores
    .filter(store => store.id.startsWith('db$'))
    .map(store => store.id);
}

export async function materializeSnapshotImport(input: {
  docEngine: SnapshotImportDocEngine;
  docsService: SnapshotImportDocsService;
  pageDocIds: readonly string[];
  systemDocIds?: readonly string[];
  workspaceId: string;
}): Promise<void> {
  const systemDocIds = input.systemDocIds ?? [];
  // A database root publishes the manifest that makes its row shards visible
  // to readiness and projection consumers. Flush referenced system docs first
  // so consumers never observe a manifest whose shards are still empty.
  const databaseRootDocIds = systemDocIds.filter(isDatabaseRootDocId);
  const dependencyDocIds = systemDocIds.filter(
    docId => !isDatabaseRootDocId(docId)
  );
  for (const docId of dependencyDocIds) {
    await input.docEngine.flushAndWaitForSynced(docId);
  }
  for (const docId of databaseRootDocIds) {
    await input.docEngine.flushAndWaitForSynced(docId);
  }
  for (const docId of input.pageDocIds) {
    input.docsService.notifyDocCreated(docId);
    await input.docEngine.flushAndWaitForSynced(docId);
  }
  await input.docEngine.flushAndWaitForSynced(input.workspaceId);

  const confirmation = await Promise.all(
    input.pageDocIds.map(async docId => ({
      confirmed: await input.docsService.waitForServerConfirmed(docId, {
        timeoutMs: 30_000,
      }),
      docId,
    }))
  );
  const missing = confirmation
    .filter(result => !result.confirmed)
    .map(result => result.docId);
  if (missing.length > 0) {
    throw new Error(
      `Imported snapshot documents were not confirmed by the destination workspace: ${missing.join(', ')}`
    );
  }
}
